import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../context/ToastContext";
import LineApi, { LineRequest } from "../../services/LineApi";
import AreaApi from "../../services/AreaApi";
import ProductionUnitApi from "../../services/ProductionUnitApi";
import { useAuth } from "../../context/AuthContext";

// Design System
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import { classNames } from "primereact/utils";

const EditLineForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateMessage } = useTranslation("messages");

  const nameRef = useRef<any>(null);
  const unitRef = useRef<any>(null);
  const areaRef = useRef<any>(null);

  const [name, setName] = useState<string>("");
  const [areaId, setAreaId] = useState<string | null>(null);

  const [lines, setLines] = useState<any[]>([]);
  // opÃ§Ãµes para Ãrea dependente de Unidade
  const [areaOptions, setAreaOptions] = useState<{ id: string; name: string }[]>([]);
  // estados para dropdown hierÃ¡rquico de Unidade e Ãrea
  const { plantId } = useAuth();
  const [unitOptions, setUnitOptions] = useState<{ id: string; name: string }[]>([]);
  const [unitId, setUnitId] = useState<string | null>(null);
  const areasRequestIdRef = useRef<number>(0);

  // monitora alteraÃ§Ãµes para evitar salvar sem mudanÃ§as
  const [originalName, setOriginalName] = useState<string>("");
  const [originalAreaId, setOriginalAreaId] = useState<string | null>(null);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  // carrega todas as linhas para checar duplicidade de nome
  const fetchAllLines = async (): Promise<void> => {
    try {
      if (!plantId) {
        setLines([]);
        return;
      }
      const resp = await LineApi.GetLinesByPlant(plantId);
      setLines(resp.data);
    } catch (err) {
      setLines([]);
    }
  };

  // carrega os dados da linha pelo id
  const fetchLine = async (lineId: string): Promise<void> => {
    try {
      const resp = await LineApi.GetLineById(lineId);
      const line = resp.data;
      setName(line?.name ?? "");
      setAreaId(line?.areaId ?? null);
      
      // inicializa Unidade de ProduÃ§Ã£o da Ã¡rea existente
      if (line?.areaId) {
        const ad = (await AreaApi.GetAreaById(line.areaId)).data;
        setUnitId(ad.productionUnitId ?? ad.productionUnit?.id ?? null);
      }

      // guardar originais para checar alteraÃ§Ãµes
      setOriginalName(line?.name ?? "");
      setOriginalAreaId(line?.areaId ?? null);
    } catch (err) {
      displayToast("error", translateMessage("error"), "", false, 3000);
      // caso nÃ£o encontre a linha, volta Ã  lista
      navigate("/lines");
    }
  };

  // carrega unidades de produÃ§Ã£o da planta atual
  const loadUnits = async (): Promise<void> => {
    if (!plantId) return;
    try {
      const res = await ProductionUnitApi.GetProductUnitByPlant(plantId);
      const opts = (res.data as any[]).map(u => ({ id: u.id, name: u.name ?? u.nome }));
      setUnitOptions(opts);
      if (opts.length === 1 && !unitId) setUnitId(opts[0].id);
    } catch {
      displayToast("error", translateMessage("error"), "", false, 3000);
      setUnitOptions([]);
    }
  };

  // carrega Ã¡reas da unidade selecionada
  const loadAreas = async (uid: string): Promise<void> => {
    if (!plantId) return;
    areasRequestIdRef.current++;
    const rid = areasRequestIdRef.current;
    try {
      const res = await AreaApi.GetAreaByPlantIdStatic(plantId);
      const all = res.data as any[];
      const filtered = all.filter(a => a.productionUnitId === uid || a.productionUnit?.id === uid);
      const opts = filtered.map(a => ({ id: a.id, name: a.name ?? a.nome }));
      if (rid === areasRequestIdRef.current) setAreaOptions(opts);
      if (opts.length === 1 && !areaId) setAreaId(opts[0].id);
    } catch {
      displayToast("error", translateMessage("error"), "", false, 3000);
      setAreaOptions([]);
    }
  };

  useEffect(() => {
    fetchAllLines();

    if (!id) {
      // se nÃ£o veio id, volta para listagem
      navigate("/lines");
      return;
    }
    fetchLine(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, plantId]);

  useEffect(() => {
    loadUnits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantId]);

  useEffect(() => {
    if (unitId) loadAreas(unitId);
    else setAreaOptions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId]);

  const handleCancel = (): void => {
    setShowConfirmCancel(true);
  };

  const handleSave = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault();
    const trimmed = name?.trim() ?? "";
    
    if (!trimmed || !unitId || !areaId) {
      displayToast("warn", translateForm("warning"), translateForm("fillInAllRequiredFields"));
      if (!unitId) unitRef.current?.focus();
      else if (!areaId) areaRef.current?.focus();
      else nameRef.current?.focus();
      return;
    }

    // se nÃ£o houve alteraÃ§Ã£o, avisa e nÃ£o envia
    const unchanged =
      trimmed === (originalName ?? "") &&
      (areaId ?? null) === (originalAreaId ?? null);

    if (unchanged) {
      displayToast("warn", translateForm("warning"), translateForm("noChanges") ?? "Nenhuma alteraÃ§Ã£o detectada");
      return;
    }

    // verifica duplicidade de nome (ignorando a prÃ³pria linha)
    const nameExists = lines.some(
      (ln) =>
        ln.name &&
        String(ln.name).toLowerCase() === trimmed.toLowerCase() &&
        String(ln.id) !== String(id),
    );

    if (nameExists) {
      displayToast("error", translateMessage("error"), translateMessage("duplicateLineError"), false, 3000);
      return;
    }

    const payload: LineRequest = {
      id: undefined,
      name: trimmed,
      areaId: areaId ?? undefined,
    };

    try {
      if (!id) throw new Error("ID da linha ausente.");
      await LineApi.Update(id, payload);
      displayToast("success", translateForm("Success"), translateMessage("updateSuccess"), false, 3000);
      navigate("/lines");
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        translateMessage("error");
      displayToast("error", translateMessage("error"), String(msg), false, 4000);
    }
  };

  return (
    <div>
      <FormCard
        title={translateForm("editLine") ?? translateForm("registerLine")}
        icon="edit"
        infoIcon="info"
        infoText="Edite os dados primÃ¡rios da linha de produÃ§Ã£o."
        submitButtonLabel={translateForm("save") ?? translateForm("register")}
        cancelButtonLabel={translateForm("Cancel")}
        onSubmit={handleSave}
        onCancel={handleCancel}
        numColumns={6}
      >
        {/* Unidade de ProduÃ§Ã£o */}
        <div style={{ gridColumn: "span 3" }}>
          <Input
            ref={unitRef}
            type="select"
            label={translateForm("productionUnit")}
            required={true}
            value={unitId}
            options={unitOptions}
            optionLabel="name"
            optionValue="id"
            placeholder={translateForm("selectUnit")}
            className={classNames("w-full", { "p-invalid border-red": !unitId })}
            disabled={true} // Seguindo o padrÃ£o de evitar mudar hierarquia superior
            onChange={(val: any) => setUnitId(val)}
          />
        </div>

        {/* Ãrea */}
        <div style={{ gridColumn: "span 3" }}>
          <Input
            ref={areaRef}
            type="select"
            label={translateForm("area")}
            required={true}
            value={areaId}
            options={areaOptions}
            optionLabel="name"
            optionValue="id"
            placeholder={translateForm("selectArea")}
            className={classNames("w-full", { "p-invalid border-red": !areaId })}
            disabled={true} // Seguindo o padrÃ£o de evitar mudar hierarquia superior
            onChange={(val: any) => setAreaId(val)}
          />
        </div>

        {/* Nome */}
        <div style={{ gridColumn: "span 6" }}>
          <Input
            ref={nameRef}
            type="text"
            label={translateForm("name")}
            required={true}
            value={name}
            onChange={(val: any) => setName(val)}
            placeholder={translateForm("name")}
            className={classNames("w-full", { "p-invalid border-red": !name.trim() })}
          />
        </div>
      </FormCard>

      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/lines")}
      />
    </div>
  );
};

export default EditLineForm;
